import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ErrorCode, errorTypes, handleDownloadError, QuarterSelectorType } from './historical-stats.helper';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Workbook, Worksheet } from 'exceljs';
import saveAs from 'file-saver';
import {
  DataObject,
  HistoricalStatQuery,
  PropertySetsHttpService,
} from '../../services/http/property-sets-http.service';
import { catchError, EMPTY, switchMap } from 'rxjs';
import { User } from '@miq/user';
import { STATE_PROVIDER, StateProvider } from '../../services/state-provider/state-provider';
import { PropertySetsState } from '../../property-sets-dialog/property-sets-dialog';
import { ConfirmDialogComponent, DialogService } from '@cbre/components';
import { LoggerFactory } from '@cbre/logger';
import { HttpErrorResponse } from '@angular/common/http';
import { CbreAlertService } from '@cbre/alerts';
import { L10nTranslationService } from 'angular-l10n';
import { EntityType } from '@miq/domain';
import { PropertySet } from '@miq/domain/property-sets';

@UntilDestroy()
@Component({
  selector: 'miq-historical-detail',
  templateUrl: './historical-detail.component.html',
  styleUrls: ['./historical-detail.component.scss'],
  standalone: false,
})
export class HistoricalDetailComponent implements OnInit {
  @Input() propertySet: PropertySet;
  periodTypeCtrl: FormControl<QuarterSelectorType>;
  maxDate = new Date();
  periodCtrl = new FormControl();
  prevPeriodType: QuarterSelectorType = QuarterSelectorType.Quarter;
  includeHistory = false;
  user: User;
  exportId: number;
  isDownloading = false;
  constructor(
    private propertySetsHttpService: PropertySetsHttpService,
    @Inject(STATE_PROVIDER) private propertySetsStateProvider: StateProvider<PropertySetsState>,
    private cbreAlertService: CbreAlertService,
    private dialogService: DialogService,
    private translationService: L10nTranslationService
  ) {}

  ngOnInit(): void {
    const savedPeriodType = this.loadPeriodType();
    this.periodTypeCtrl = new FormControl(savedPeriodType);
    this.user = this.propertySetsStateProvider.snapshot().userInfo;
    this.periodTypeChange();
  }

  public periodTypeChange(): void {
    this.savePeriodType(this.periodTypeCtrl.value);
  }

  public downloadReport() {
    if (this.periodCtrl?.value?.lte != null && this.periodCtrl?.value?.gte != null && this.periodTypeCtrl.value) {
      this.isDownloading = true;
      const payload = this.payload();
      this.propertySetsHttpService
        .initHistoricalExport({
          ...payload,
          isClipboard: false,
          propertySetRecordId: this.propertySet?.fingerprint,
          isHistorical: this.includeHistory,
          periodType: this.periodTypeCtrl.value,
        })
        .pipe(
          switchMap(exportId => {
            this.exportId = exportId;
            const options = {
              ...payload.options,
              propertySetRecordId: this.propertySet?.fingerprint,
              isHistorical: this.includeHistory,
              periodType: this.periodTypeCtrl.value,
            };
            return this.propertySetsHttpService.bulkExport({ ...payload, options, isClipBoard: false, exportId });
          }),
          catchError(err => {
            this.failedTransaction(this.exportId, errorTypes[err.error?.code]);
            return this.handleError(err);
          })
        )
        .subscribe((resp: any) => {
          if (!resp) {
            this.handleBulkError({ error: 'No response received' }, this.exportId);
            return;
          }

          const { data, fileName, err } = resp;
          if (err) {
            this.handleBulkError(err, this.exportId);
            return;
          }
          if (!!data && !!fileName) {
            const { recordData, exportID } = JSON.parse(data);
            const workbook: Workbook = new Workbook();
            const worksheet: Worksheet = workbook.addWorksheet(EntityType.Property, {
              properties: { defaultColWidth: 22 },
            });
            worksheet.addRows(recordData);
            workbook.csv.writeBuffer().then(data => {
              // Use the BOM (Byte Order Mark) to help some applications recognize the encoding.
              const bom = '\uFEFF';
              const utf8Data = new TextEncoder().encode(bom + data);
              const blob = new Blob([utf8Data], { type: 'text/csv;charset=utf-8' });
              saveAs(blob, fileName);
            });
            this.completeTransaction(exportID ?? this.exportId); // completes transaction.
            return;
          }
          this.failedTransaction(this.exportId, null);
        });
    }
  }

  private handleBulkError(err: DataObject, exportId: number) {
    const customError = err?.error;
    let errType = ErrorCode.UnknownIssue;
    if (customError != null) {
      errType =
        typeof customError === 'string' || customError?.message == null ? err?.status : customError?.message?.code;
    }
    this.failedTransaction(exportId, errorTypes[errType]);
  }

  private failedTransaction(exportId: number, message: string) {
    this.completeTransaction(exportId, true); // transaction failed.
    this.cbreAlertService.showErrorSnackBar_l10n(message ?? 'recordsSearch.unknownIssue', {
      duration: 3000,
    });
  }

  private completeTransaction(exportID, failed = false) {
    this.isDownloading = false;
    if (!exportID) {
      return;
    }
    this.exportId = null;
    return this.propertySetsHttpService.completeExport(exportID, failed).subscribe();
  }

  private handleError(err: Error | HttpErrorResponse) {
    LoggerFactory.getLogger().error(err);
    const { name, message } = err;
    if (name === 'groupingError') {
      this.cbreAlertService.showErrorSnackBar_l10n(message, {
        duration: 3000,
      });
      return EMPTY;
    } else {
      return this.handleDownloadError(err);
    }
  }

  private handleDownloadError(err) {
    const { isDownloadLimitError, message } = handleDownloadError(
      err.error,
      EntityType.Property,
      this.translationService
    );
    if (isDownloadLimitError) {
      return this.dialogService.openDialog(ConfirmDialogComponent, {
        message,
        confirmText: 'OK',
      });
    } else {
      this.cbreAlertService.showErrorSnackBar_l10n('recordsSearch.errWhileExportingRecords', {
        duration: 3000,
      });
      return EMPTY;
    }
  }

  private payload(): HistoricalStatQuery {
    return {
      query: {
        entity: EntityType.Property,
        offset: 0,
        limit: this.propertySet?.no_of_records || -1,
        showAllUsages: true,
        navigationSource: 'default',
        viewEnabled: 'table',
        userId: this.user?.id,
      },
      options: {
        property: 'usages',
        column: 'all',
        allowDuplicates: true,
        period: {
          lte: this.periodCtrl.value.lte,
          gte: this.periodCtrl.value.gte,
        },
        periodType: this.periodTypeCtrl.value,
        propertySetRecordId: this.propertySet?.fingerprint,
        isHistorical: this.includeHistory,
        userId: this.user?.id,
        propertyType: '',
      },
      type: 'historicalStats',
    };
  }

  private savePeriodType(value: QuarterSelectorType) {
    localStorage.setItem('periodType', JSON.stringify(value));
  }

  private loadPeriodType(): QuarterSelectorType | null {
    const value = localStorage.getItem('periodType');
    return value ? (JSON.parse(value) as QuarterSelectorType) : QuarterSelectorType.Quarter;
  }
}
